import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { accountingApi, reportsApi } from '../../api';
import {
  PageHeader,
  LoadingState,
  ErrorState,
  EmptyState,
} from '../../components/common';
import { fmtCurrency, fmtMoney } from '../../utils/currency';

type Tab = 'tb' | 'bs' | 'is' | 'cf' | 'gl';

/**
 * Financial reports — FR-ACC-06
 * Trial balance, balance sheet, income statement, cash flow, general ledger.
 * Phase B: proper financial statement typography.
 */
export function ReportsPage() {
  const [tab, setTab] = useState<Tab>('tb');
  const today = new Date().toISOString().slice(0, 10);
  const ymStart = today.slice(0, 7) + '-01';

  const [asOfDate, setAsOfDate] = useState(today);
  const [fromDate, setFromDate] = useState(ymStart);
  const [toDate, setToDate] = useState(today);
  const [accountId, setAccountId] = useState('');

  const TABS: Array<{ key: Tab; label: string; short: string }> = [
    { key: 'tb', label: 'Trial balance',    short: 'TB' },
    { key: 'bs', label: 'Balance sheet',    short: 'BS' },
    { key: 'is', label: 'Income statement', short: 'IS' },
    { key: 'cf', label: 'Cash flow',        short: 'CF' },
    { key: 'gl', label: 'General ledger',   short: 'GL' },
  ];

  return (
    <div className="px-8 py-10 max-w-[96rem] mx-auto">
      <PageHeader
        eyebrow="Accounting · Reporting"
        title="Financial reports"
        subtitle="Formal statements generated from the general ledger — use for period-close reviews, board reporting, and audits."
      />

      {/* Tab strip */}
      <div className="card p-1.5 mb-6 inline-flex gap-1 border-slate-200 flex-wrap">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={clsx(
              'px-4 py-1.5 text-sm font-semibold rounded-pill transition-all',
              tab === t.key
                ? 'bg-finance-50 text-finance-800 ring-1 ring-inset ring-finance-200'
                : 'text-slate-600 hover:bg-slate-50'
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Shared date-range controls */}
      <div className="card p-4 mb-6 flex gap-4 items-end flex-wrap border-slate-200">
        {(tab === 'tb' || tab === 'bs') && (
          <div>
            <label className="label">As of date</label>
            <input
              type="date"
              className="input w-44"
              value={asOfDate}
              onChange={(e) => setAsOfDate(e.target.value)}
            />
          </div>
        )}
        {(tab === 'is' || tab === 'cf' || tab === 'gl') && (
          <>
            <div>
              <label className="label">From</label>
              <input
                type="date"
                className="input w-44"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>
            <div>
              <label className="label">To</label>
              <input
                type="date"
                className="input w-44"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>
          </>
        )}
        {tab === 'gl' && <GlAccountPicker value={accountId} onChange={setAccountId} />}
        <div className="flex-1" />
        <ExportMenu
          tab={tab}
          asOfDate={asOfDate}
          fromDate={fromDate}
          toDate={toDate}
          accountId={accountId}
        />
        <div className="text-xs text-slate-500 max-w-[180px] text-right">
          Reports are computed live from posted journal entries.
        </div>
      </div>

      {tab === 'tb' && <TrialBalance asOfDate={asOfDate} />}
      {tab === 'bs' && <BalanceSheet asOfDate={asOfDate} />}
      {tab === 'is' && <IncomeStatement fromDate={fromDate} toDate={toDate} />}
      {tab === 'cf' && <CashFlow fromDate={fromDate} toDate={toDate} />}
      {tab === 'gl' && <GeneralLedger accountId={accountId} fromDate={fromDate} toDate={toDate} />}
    </div>
  );
}

function ExportMenu({ tab, asOfDate, fromDate, toDate, accountId }: {
  tab: Tab; asOfDate: string; fromDate: string; toDate: string; accountId: string;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  const typeMap: Record<Tab, string> = {
    tb: 'finance.trial-balance',
    bs: 'finance.balance-sheet',
    is: 'finance.income-statement',
    cf: 'finance.cash-flow',
    gl: 'finance.general-ledger',
  };
  const params: Record<string, any> =
    tab === 'tb' || tab === 'bs' ? { asOfDate }
    : tab === 'gl' ? { accountId, fromDate, toDate }
    : { fromDate, toDate };

  const disabled = tab === 'gl' && !accountId;

  const run = async (format: 'xlsx' | 'pdf' | 'csv') => {
    if (disabled) return;
    setBusy(format); setOpen(false);
    try {
      await reportsApi.downloadExport(format, typeMap[tab], params);
    } catch (e) {
      // surface failure unobtrusively
      alert('Export failed. Please try again.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        disabled={disabled || !!busy}
        className={clsx(
          'px-4 py-2 text-sm font-semibold rounded-lg inline-flex items-center gap-2 transition-all',
          disabled
            ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
            : 'bg-finance-700 text-white hover:bg-finance-800 shadow-sm'
        )}
        title={disabled ? 'Select an account first' : 'Export this report'}
      >
        {busy ? 'Exporting…' : 'Export'}
        <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor"><path d="M5.5 7.5L10 12l4.5-4.5" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>
      </button>
      {open && !disabled && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-1 w-44 bg-white rounded-lg shadow-lg ring-1 ring-slate-200 z-20 overflow-hidden">
            {[
              { f: 'pdf' as const, label: 'PDF document', icon: '📄' },
              { f: 'xlsx' as const, label: 'Excel workbook', icon: '📊' },
              { f: 'csv' as const, label: 'CSV (raw data)', icon: '📋' },
            ].map((o) => (
              <button
                key={o.f}
                onClick={() => run(o.f)}
                className="w-full px-4 py-2.5 text-sm text-left text-slate-700 hover:bg-finance-50 flex items-center gap-2.5 transition-colors"
              >
                <span>{o.icon}</span>{o.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function GlAccountPicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const q = useQuery({ queryKey: ['accounts'], queryFn: () => accountingApi.listAccounts() });
  return (
    <div className="flex-1 min-w-[300px]">
      <label className="label">Account</label>
      <select className="input" value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">Select account…</option>
        {q.data?.map((a: any) => (
          <option key={a.id} value={a.id}>{a.code} — {a.name}</option>
        ))}
      </select>
    </div>
  );
}

// ============================================================
// StatementHeader — shared across all 5 views
// ============================================================
function StatementHeader({
  title, period, balanced,
}: {
  title: string;
  period: string;
  balanced?: boolean;
}) {
  return (
    <div className="flex items-start justify-between mb-5 flex-wrap gap-3">
      <div>
        <div className="eyebrow text-finance-700 mb-1">Financial statement</div>
        <h2 className="font-display font-bold text-2xl text-slate-900 tracking-tight">{title}</h2>
        <div className="font-mono text-sm text-slate-500 mt-1">{period}</div>
      </div>
      {balanced !== undefined && (
        <div className={clsx(
          'inline-flex items-center font-semibold text-xs uppercase tracking-wider px-3 py-1 rounded-pill ring-1 ring-inset',
          balanced
            ? 'bg-sage-50 text-sage-700 ring-sage-200'
            : 'bg-red-50 text-red-700 ring-red-200'
        )}>
          {balanced ? '✓ Balanced' : '✗ Out of balance'}
        </div>
      )}
    </div>
  );
}

// ============================================================
// Trial Balance
// ============================================================
function TrialBalance({ asOfDate }: { asOfDate: string }) {
  const q = useQuery({
    queryKey: ['tb', asOfDate],
    queryFn: () => accountingApi.trialBalance({ asOfDate }),
  });

  if (q.isLoading) return <div className="card"><LoadingState rows={5} /></div>;
  if (q.error) return <ErrorState error={q.error} />;
  const d = q.data;

  return (
    <div className="card p-8 border-slate-200">
      <StatementHeader
        title="Trial Balance"
        period={`As of ${new Date(d.asOfDate).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}`}
        balanced={d.totals.balanced}
      />

      {d.accounts.length === 0 ? (
        <EmptyState message="No posted activity as of this date." />
      ) : (
        <div className="rounded-lg border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-slate-600">Code</th>
                <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-slate-600">Account</th>
                <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-slate-600">Type</th>
                <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-slate-600">Debit</th>
                <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-slate-600">Credit</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {d.accounts.map((a: any, i: number) => (
                <tr key={a.accountId} className={i % 2 ? 'bg-slate-50/40' : 'bg-white'}>
                  <td className="px-4 py-2 font-mono text-xs font-semibold text-slate-900">{a.code}</td>
                  <td className="px-4 py-2 text-slate-900">{a.name}</td>
                  <td className="px-4 py-2 text-2xs text-slate-500 uppercase tracking-wider">{a.type}</td>
                  <td className="px-4 py-2 text-right font-mono tabular-nums">
                    {a.debitBalance > 0 ? (
                      <span className="text-slate-900">{fmtCurrency(a.debitBalance)}</span>
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right font-mono tabular-nums">
                    {a.creditBalance > 0 ? (
                      <span className="text-slate-900">{fmtCurrency(a.creditBalance)}</span>
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-slate-100 border-t-2 border-slate-800">
                <td colSpan={3} className="px-4 py-3 eyebrow text-slate-700 text-right">Totals</td>
                <td className="px-4 py-3 text-right font-mono font-display font-bold text-lg text-slate-900 tabular-nums">
                  ${d.totals.debit.toFixed(2)}
                </td>
                <td className="px-4 py-3 text-right font-mono font-display font-bold text-lg text-slate-900 tabular-nums">
                  ${d.totals.credit.toFixed(2)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Balance Sheet
// ============================================================
function BalanceSheet({ asOfDate }: { asOfDate: string }) {
  const q = useQuery({
    queryKey: ['bs', asOfDate],
    queryFn: () => accountingApi.balanceSheet(asOfDate),
  });

  if (q.isLoading) return <div className="card"><LoadingState rows={5} /></div>;
  if (q.error) return <ErrorState error={q.error} />;
  const d = q.data;

  return (
    <div className="card p-8 border-slate-200">
      <StatementHeader
        title="Balance Sheet"
        period={`As of ${new Date(d.asOfDate).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}`}
        balanced={d.totals.balanced}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <StatementSection
          title="Assets"
          total={d.totals.assets}
          accent="finance"
        >
          <AccountList accounts={d.assets} />
        </StatementSection>

        <div>
          <StatementSection
            title="Liabilities"
            total={d.totals.liabilities}
            accent="amber"
          >
            <AccountList accounts={d.liabilities} />
          </StatementSection>

          <div className="mt-6">
            <StatementSection
              title="Equity"
              total={d.totals.equity}
              accent="purple"
            >
              <AccountList accounts={d.equity} />
              <div className="flex justify-between text-sm text-slate-600 py-1 pl-2 italic">
                <span>Retained earnings <span className="text-slate-400 text-xs">(net income since inception)</span></span>
                <span className="font-mono tabular-nums">{fmtCurrency(d.retainedEarnings)}</span>
              </div>
            </StatementSection>
          </div>

          {/* Grand total */}
          <div className="mt-6 pt-4 border-t-4 border-slate-900 double-border-b flex justify-between items-baseline">
            <span className="font-display font-bold text-slate-900">Liabilities + Equity</span>
            <span className="font-mono font-display font-bold text-xl text-slate-900 tabular-nums tracking-tight">
              ${d.totals.liabilitiesAndEquity.toFixed(2)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Income Statement
// ============================================================
function IncomeStatement({ fromDate, toDate }: { fromDate: string; toDate: string }) {
  const q = useQuery({
    queryKey: ['is', fromDate, toDate],
    queryFn: () => accountingApi.incomeStatement({ fromDate, toDate }),
  });

  if (q.isLoading) return <div className="card"><LoadingState rows={5} /></div>;
  if (q.error) return <ErrorState error={q.error} />;
  const d = q.data;
  const t = d.totals;

  return (
    <div className="card p-8 max-w-3xl border-slate-200">
      <StatementHeader
        title="Income Statement"
        period={`${new Date(d.period.from).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })} → ${new Date(d.period.to).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}`}
      />

      <StatementSection title="Revenue" total={t.revenue} accent="sage">
        <AccountList accounts={d.revenue.filter((a: any) => a.balance !== 0)} />
      </StatementSection>

      <div className="mt-6">
        <StatementSection title="Cost of Goods Sold" total={t.cogs} accent="orange">
          <AccountList accounts={d.cogs.filter((a: any) => a.balance !== 0)} />
        </StatementSection>
      </div>

      {/* Gross profit */}
      <div className="mt-6 pt-3 border-t-2 border-slate-400 flex justify-between items-baseline">
        <div>
          <span className="font-semibold text-slate-900">Gross Profit</span>
          {t.grossMarginPct != null && (
            <span className="ml-2 text-sm text-slate-500 font-mono tabular-nums">
              ({t.grossMarginPct}%)
            </span>
          )}
        </div>
        <span className={clsx(
          'font-mono font-semibold text-lg tabular-nums',
          t.grossProfit >= 0 ? 'text-slate-900' : 'text-danger'
        )}>
          ${t.grossProfit.toFixed(2)}
        </span>
      </div>

      <div className="mt-6">
        <StatementSection title="Operating Expenses" total={t.expenses} accent="danger">
          <AccountList accounts={d.expenses.filter((a: any) => a.balance !== 0)} />
        </StatementSection>
      </div>

      {/* Net income */}
      <div className="mt-6 pt-4 border-t-4 border-slate-900 flex justify-between items-baseline">
        <div>
          <span className="font-display font-bold text-slate-900">Net Income</span>
          {t.netMarginPct != null && (
            <span className="ml-2 text-sm text-slate-500 font-mono tabular-nums">
              ({t.netMarginPct}%)
            </span>
          )}
        </div>
        <span className={clsx(
          'font-mono font-display font-bold text-2xl tabular-nums tracking-tight',
          t.netIncome >= 0 ? 'text-sage-700' : 'text-danger'
        )}>
          ${t.netIncome.toFixed(2)}
        </span>
      </div>
    </div>
  );
}

// ============================================================
// Cash Flow — indirect method
// ============================================================
function CashFlow({ fromDate, toDate }: { fromDate: string; toDate: string }) {
  const q = useQuery({
    queryKey: ['cf', fromDate, toDate],
    queryFn: () => accountingApi.cashFlow({ fromDate, toDate }),
  });

  if (q.isLoading) return <div className="card"><LoadingState rows={5} /></div>;
  if (q.error) return <ErrorState error={q.error} />;
  const d = q.data;

  const adjLine = (label: string, amount: number) => (
    <div className="flex justify-between py-1 pl-8 text-sm">
      <span className="text-slate-600">{label}</span>
      <span className={clsx(
        'font-mono tabular-nums',
        amount < 0 ? 'text-danger' : 'text-slate-800'
      )}>
        {amount < 0 ? `(${Math.abs(amount).toFixed(2)})` : amount.toFixed(2)}
      </span>
    </div>
  );

  return (
    <div className="card p-8 max-w-3xl border-slate-200">
      <StatementHeader
        title="Cash Flow Statement"
        period={`${new Date(d.period.from).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })} → ${new Date(d.period.to).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}`}
      />
      <div className="text-xs text-slate-500 -mt-2 mb-6 italic">Indirect method</div>

      {/* Operating */}
      <div className="mb-6">
        <h3 className="eyebrow text-slate-700 border-b border-slate-300 pb-1 mb-2">Operating activities</h3>
        <div className="flex justify-between py-1 font-semibold">
          <span className="text-slate-900">Net Income</span>
          <span className="font-mono tabular-nums text-slate-900">
            ${d.operating.netIncome.toFixed(2)}
          </span>
        </div>
        <div className="text-xs text-slate-500 italic mt-2 pl-4 mb-1">Working capital changes</div>
        {adjLine('Change in Accounts Receivable', d.operating.adjustments.changeInAR)}
        {adjLine('Change in Inventory', d.operating.adjustments.changeInInventory)}
        {adjLine('Change in Accounts Payable', d.operating.adjustments.changeInAP)}
        {adjLine('Change in Tax Payable', d.operating.adjustments.changeInTaxPayable)}
        {adjLine('Change in Payroll Payable', d.operating.adjustments.changeInPayrollPayable)}
        <div className="flex justify-between pt-2 mt-2 border-t border-slate-300 font-semibold">
          <span className="text-slate-900">Operating Cash Flow</span>
          <span className="font-mono tabular-nums text-slate-900">
            ${d.operating.total.toFixed(2)}
          </span>
        </div>
      </div>

      {/* Investing */}
      <div className="mb-6">
        <h3 className="eyebrow text-slate-700 border-b border-slate-300 pb-1 mb-2">Investing activities</h3>
        <div className="flex justify-between py-1 text-sm">
          <span className="text-slate-800">Net investing</span>
          <span className="font-mono tabular-nums">{fmtCurrency(d.investing.total)}</span>
        </div>
        <div className="text-2xs text-slate-500 italic pl-4">{d.investing.note}</div>
      </div>

      {/* Financing */}
      <div className="mb-6">
        <h3 className="eyebrow text-slate-700 border-b border-slate-300 pb-1 mb-2">Financing activities</h3>
        <div className="flex justify-between py-1 text-sm">
          <span className="text-slate-800">Net financing</span>
          <span className="font-mono tabular-nums">{fmtCurrency(d.financing.total)}</span>
        </div>
        <div className="text-2xs text-slate-500 italic pl-4">{d.financing.note}</div>
      </div>

      {/* Net change */}
      <div className="pt-4 border-t-4 border-slate-900 flex justify-between items-baseline">
        <span className="font-display font-bold text-slate-900">Net Change in Cash</span>
        <span className={clsx(
          'font-mono font-display font-bold text-xl tabular-nums tracking-tight',
          d.netChange >= 0 ? 'text-sage-700' : 'text-danger'
        )}>
          ${d.netChange.toFixed(2)}
        </span>
      </div>

      <div className="flex justify-between text-sm text-slate-600 mt-2">
        <span>Actual cash account movement</span>
        <span className="font-mono tabular-nums">{fmtCurrency(d.actualCashMovement)}</span>
      </div>

      {Math.abs(d.reconciliationGap) > 0.01 && (
        <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-4 text-xs text-amber-900">
          <div className="font-semibold mb-1">
            Reconciliation gap: ${d.reconciliationGap.toFixed(2)}
          </div>
          <div className="leading-relaxed">
            Difference between computed cash flow and actual cash movement — usually explained by un-categorized investing/financing activity (fixed asset purchases, loans, equity issues). Review and classify those journal entries manually.
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// General Ledger — per-account drill
// ============================================================
function GeneralLedger({ accountId, fromDate, toDate }:
    { accountId: string; fromDate: string; toDate: string }) {
  const q = useQuery({
    queryKey: ['gl', accountId, fromDate, toDate],
    queryFn: () => accountingApi.generalLedger({ accountId, fromDate, toDate }),
    enabled: !!accountId,
  });

  if (!accountId) {
    return (
      <div className="card">
        <EmptyState
          message="Select an account"
          hint="Pick an account above to view its general ledger activity with running balance."
        />
      </div>
    );
  }
  if (q.isLoading) return <div className="card"><LoadingState rows={5} /></div>;
  if (q.error) return <ErrorState error={q.error} />;
  const d = q.data;

  return (
    <div className="card p-8 border-slate-200">
      {/* Account header */}
      <div className="flex items-start justify-between mb-5 flex-wrap gap-3">
        <div>
          <div className="eyebrow text-finance-700 mb-1">General ledger</div>
          <h2 className="font-display font-bold text-2xl text-slate-900 tracking-tight">
            <span className="font-mono text-lg text-slate-500 mr-3">{d.account.code}</span>
            {d.account.name}
          </h2>
          <div className="flex items-center gap-2 mt-1">
            <span className="font-mono text-2xs font-semibold uppercase tracking-wider text-slate-700">
              {d.account.type}
            </span>
            <span className="text-xs text-slate-500">
              · Normal balance: <span className="font-semibold">{d.account.normalBalance}</span>
            </span>
            <span className="text-xs text-slate-500">
              · {new Date(d.period.from).toLocaleDateString()} – {new Date(d.period.to).toLocaleDateString()}
            </span>
          </div>
        </div>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <GlSummaryTile label="Opening" value={`${fmtCurrency(d.openingBalance)}`} />
        <GlSummaryTile label="Transactions" value={d.totals.transactionCount} />
        <GlSummaryTile label="Total debit" value={`${fmtCurrency(d.totals.debit)}`} />
        <GlSummaryTile label="Total credit" value={`${fmtCurrency(d.totals.credit)}`} />
        <GlSummaryTile label="Closing" value={`${fmtCurrency(d.closingBalance)}`} accent="finance" />
      </div>

      {d.transactions.length === 0 ? (
        <EmptyState message="No transactions in this period." />
      ) : (
        <div className="rounded-lg border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-slate-600">Date</th>
                  <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-slate-600">JE #</th>
                  <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-slate-600">Description</th>
                  <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-slate-600">Source</th>
                  <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-slate-600">Debit</th>
                  <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-slate-600">Credit</th>
                  <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-slate-600">Running</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {d.transactions.map((t: any, i: number) => (
                  <tr key={i} className={i % 2 ? 'bg-slate-50/40' : 'bg-white'}>
                    <td className="px-4 py-2 font-mono text-xs text-slate-600 tabular-nums">
                      {new Date(t.entryDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                    </td>
                    <td className="px-4 py-2 font-mono text-xs font-semibold text-slate-900">{t.entryNumber}</td>
                    <td className="px-4 py-2 text-slate-700 max-w-sm truncate">{t.description}</td>
                    <td className="px-4 py-2 text-xs text-slate-500">
                      {t.sourceType ?? <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-4 py-2 text-right font-mono tabular-nums">
                      {t.debit > 0 ? <span className="text-slate-900">{fmtCurrency(t.debit)}</span> : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-4 py-2 text-right font-mono tabular-nums">
                      {t.credit > 0 ? <span className="text-slate-900">{fmtCurrency(t.credit)}</span> : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-4 py-2 text-right font-mono font-semibold text-slate-900 tabular-nums">
                      ${t.runningBalance.toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Shared statement components
// ============================================================
function StatementSection({
  title, total, accent = 'finance', children,
}: {
  title: string;
  total: number;
  accent?: 'finance' | 'sage' | 'amber' | 'orange' | 'danger' | 'purple';
  children: React.ReactNode;
}) {
  const accentTone = {
    finance: 'text-finance-700',
    sage:    'text-sage-700',
    amber:   'text-amber-700',
    orange:  'text-orange-700',
    danger:  'text-danger',
    purple:  'text-purple-700',
  }[accent];

  return (
    <div>
      <h3 className={clsx('eyebrow border-b border-slate-300 pb-1 mb-2', accentTone)}>
        {title}
      </h3>
      {children}
      <div className="flex justify-between font-semibold text-sm border-t border-slate-300 pt-2 mt-1">
        <span className="text-slate-900">Total {title.toLowerCase()}</span>
        <span className="font-mono tabular-nums text-slate-900">{fmtCurrency(total)}</span>
      </div>
    </div>
  );
}

function AccountList({ accounts }: { accounts: any[] }) {
  if (!accounts?.length) {
    return <div className="text-xs text-slate-400 italic py-2 pl-2">No balances</div>;
  }
  return (
    <div className="divide-y divide-slate-100">
      {accounts.map((a: any) => (
        <div key={a.code} className="flex justify-between py-1.5 pl-2 hover:bg-slate-50/60 rounded">
          <div className="flex gap-3 min-w-0">
            <span className="font-mono text-xs text-slate-500 tabular-nums flex-shrink-0 w-20">{a.code}</span>
            <span className="text-sm text-slate-800 truncate">{a.name}</span>
          </div>
          <span className="font-mono text-sm tabular-nums text-slate-900 flex-shrink-0">
            ${a.balance.toFixed(2)}
          </span>
        </div>
      ))}
    </div>
  );
}

function GlSummaryTile({
  label, value, accent,
}: { label: string; value: number | string; accent?: 'finance' }) {
  return (
    <div className={clsx(
      'rounded-xl p-3 border',
      accent === 'finance'
        ? 'bg-finance-50 border-finance-200'
        : 'bg-slate-50 border-slate-200'
    )}>
      <div className={clsx(
        'eyebrow',
        accent === 'finance' ? 'text-finance-700' : 'text-slate-500'
      )}>
        {label}
      </div>
      <div className={clsx(
        'font-mono tabular-nums mt-1',
        accent === 'finance' ? 'font-display font-bold text-xl' : 'font-semibold text-sm',
        accent === 'finance' ? 'text-slate-900' : 'text-slate-900'
      )}>
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
    </div>
  );
}
